// app/providers.js
'use client';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import React, { Suspense, useEffect } from 'react'; // Add the missing import statement for React
import PostHogPageView from './PHPageView';
import { z } from 'zod';
import { capture, identifyUser } from '@/config/captureUserEvent';
import { EventNames } from '@/config/eventNames';
import { AdTrackingProvider } from '@/providers/AdTrackingProvider';
import { AdAttributionRefresher } from '@/providers/AdAttributionRefresher';
export function PostHogClientSideProviders({
  children,
}: {
  children: React.ReactNode;
}) {
  if (process.env.NEXT_PUBLIC_POSTHOG_KEY) {
    console.log('Initializing PostHog');
    posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY, {
      // Same-origin relay (next.config.mjs rewrites), so ad blockers do not drop events.
      api_host: '/relay',
      // Follows the region the relay forwards to (next.config.mjs), so links from
      // the toolbar and replays never point at the other region.
      ui_host: (process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://eu.i.posthog.com').replace('.i.posthog.com', '.posthog.com'),
      // Real-user LCP, INP and CLS: the mobile landing pages hold people for a
      // median of 23 seconds, and load speed is one suspect.
      capture_performance: { web_vitals: true },
      capture_pageview: false, // Disable automatic pageview capture, as we capture manually
      capture_pageleave: true, // Enable pageleave capture,
      session_recording: {
        maskAllInputs: false,
        maskInputOptions: {
          password: true, // Highly recommended as a minimum!!
          color: false,
          date: false,
          'datetime-local': false,
          email: false,
          month: false,
          number: false,
          range: false,
          search: false,
          tel: false,
          text: false,
          time: false,
          url: false,
          week: false,
          textarea: false,
          select: false,
        },
      },
    });
  } else {
    console.log('PostHog not initialized');
  }

  function onTidioChatApiPreFormFilled({ data }: any) {
    if (data) {
      const email = data.form_data.email;
      z.string().email().parse(email);
      identifyUser(email);
      capture(EventNames.tidioChatPreFormFilled);
    }
  }

  useEffect(() => {
    document.addEventListener(
      'tidioChat-preFormFilled',
      onTidioChatApiPreFormFilled,
    );

    return () => {
      document.removeEventListener(
        'tidioChat-preFormFilled',
        onTidioChatApiPreFormFilled,
      );
    };
  }, []);

  // Specify the type for the 'children' prop
  return (
    <PostHogProvider client={posthog}>
      {/* Both read useSearchParams and render nothing; a Suspense boundary lets the
          pages around them prerender instead of bailing out to client rendering. */}
      <Suspense fallback={null}>
        <PostHogPageView />
        <AdAttributionRefresher />
      </Suspense>
      <AdTrackingProvider>
        {children}
      </AdTrackingProvider>
    </PostHogProvider>
  );
}
